import axios from 'axios';
import { AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios';
import theLocalStorageUtils from '@/utils/LocalStorageUtils';
import { LocalStorageKeys } from '@/constants/LocalStorageKeys';
import IServiceHelper from './IServiceHelper';

export default abstract class BaseApiService {
    private baseUrl: string;
    private retries = 0;
    private additionalHeaders: any = {};
    private includeAuthorization = true;
    private logRequest = false;

    axiosInstance = axios.create();

    constructor(baseUrl: string) {
        this.baseUrl = baseUrl;

        this.axiosInstance.interceptors.request.use(this.onRequest);
        this.axiosInstance.interceptors.response.use(this.onResponse, this.onResponseError);
    }

    abstract getServiceHelper(): IServiceHelper;

    /**
     * Add extra headers as an object. Will add the given to any existing additional headers.
     * Also, response handling clears all additional headers (data or error).
     * @param headers
     */
    addHeaders(headers: any): void {
        this.additionalHeaders = { ...this.additionalHeaders, ...headers };
    }

    addAcceptJsonHeader() {
        this.addHeaders({ accept: 'application/json' });
    }

    addContentTypeJsonHeader() {
        this.addHeaders({ 'Content-Type': 'application/json' });
    }

    /**
     * Allow control of including the Authorization header in calls.
     * Default is to include authorization.
     * @param include
     */
    setIncludeAuthorization(include: boolean): void {
        this.includeAuthorization = include;
    }

    setLogRequest(log: boolean): void {
        this.logRequest = log;
    }

    /**
     * Build a URI encoded string of query parameters from the given object.
     * The given object's top-level keys are used as the attributes with the object's values being URI encoded.
     * Only object values which pass a query value check are included in the result.
     * @see isValidQueryValue for the query value check
     * @param requestParams
     * @returns
     */
    buildQuery(requestParams: any): string {
        let result = '';
        if (requestParams) {
            const keys = Object.keys(requestParams);
            for (const key of keys) {
                if (this.isValidQueryValue(requestParams[key])) {
                    const trimmmed = `${requestParams[key]}`.trim();
                    result += result.length === 0 ? '?' : '&';
                    result += key + '=';
                    result += encodeURIComponent(trimmmed);
                    result = result.trim();
                }
            }
        }
        return result;
    }

    /**
     * Check's the given paramValue for validity. Param values should be a string, number or boolean.
     * Param values should have a value and not be null, undefined, NaN, -0, etc.
     * @param paramValue
     * @returns
     */
    isValidQueryValue(paramValue: any): boolean {
        if (typeof paramValue === 'string' && paramValue) {
            // Any string value other than empty
            return true;
        }
        if (typeof paramValue === 'number' && (paramValue === 0 || paramValue)) {
            // Any number value that is 0 or truthy
            return true;
        }
        if (typeof paramValue === 'boolean') {
            // Any boolean value
            return true;
        }
        return false;
    }

    /**
     * Get descriptive information from a thrown error.
     * Wrap the axiosInstance call with a try-catch block and pass the caught error to this
     * method to get back a useful string.
     * @param error
     * @returns
     */
    getErrorDescription(error: any): string {
        let result = '';
        if (error && error.response) {
            const response = error.response;
            result += `Status[${response.status}, ${response.statusText}]`;

            if (response.data) {
                const data = response.data;
                if (data.message) {
                    result += `, Message[${data.message}]`;
                }

                if (data.errors && data.errors.length > 0) {
                    result += `, Errors[`;
                    for (const errMsg of data.errors) {
                        result += `${errMsg}, `;
                    }
                    result = result.substring(0, result.length - 2);
                    result += `]`;
                }
            }
        }
        return result;
    }

    private resetAfterResponse(): void {
        this.retries = 0;
        this.additionalHeaders = {};
        this.logRequest = false;
    }

    private buildHeaders(): any {
        let headers = {};

        if (Object.keys(this.additionalHeaders).length > 0) {
            headers = { ...headers, ...this.additionalHeaders };
        }

        if (this.includeAuthorization) {
            const jwt = theLocalStorageUtils.get(LocalStorageKeys.qsAccessToken);
            if (jwt) {
                const authHeader = {
                    Authorization: `Bearer ${jwt}`
                };
                headers = { ...headers, ...authHeader };
            }
        }

        return headers;
    }

    onRequest = async (config: AxiosRequestConfig): Promise<AxiosRequestConfig> => {
        config.baseURL = this.baseUrl.toLowerCase();
        config.headers = this.buildHeaders();

        if (this.logRequest) {
            const printable = this.buildPrintableFromConfig(config);
            //console.log('onRequest(), Request[' + printable + ']');
        }
        return config;
    };

    onResponse = (response: AxiosResponse): AxiosResponse => {
        this.resetAfterResponse();
        return response;
    };

    onResponseError = async (error: AxiosError): Promise<AxiosError> => {
        // if response is unauthorized and haven't retried yet, request token data
        // and if successful retry the failed request
        if (error.response?.status === 401 && this.retries === 0) {
            console.log(`BaseApiService.onResponseError(), Unauthorized on [${error.config}], retrying`);
            const errorHelper = this.getServiceHelper();
            const success = await errorHelper.getAndSaveAuthToken();
            if (success) {
                const newJwt = theLocalStorageUtils.get(LocalStorageKeys.qsAccessToken);

                axios.defaults.headers.common['Authorization'] = `Bearer ${newJwt}`;
                const originalRequest = error.config;
                originalRequest.headers['Authorization'] = `Bearer ${newJwt}`;
                return axios.request(originalRequest);
            }
        }

        // Send information related to the error to the console
        const printable = this.buildPrintableFromError(error);
        console.log('onResponseError(), Error[' + printable + ']');

        if (error.response?.status === 500) {
            const errorHelper = this.getServiceHelper();
            const success = await errorHelper.saveErrorLog(error.response.status, error.response.statusText);
        }

        this.resetAfterResponse();
        return Promise.reject(error);
    };

    private buildPrintableFromConfig(config: AxiosRequestConfig): string {
        const printable = `Method[${config.method}], Base[${config.baseURL}], Url[${config.url}],
        Params[${JSON.stringify(config.params)}],
        Data[${JSON.stringify(config.data)}],
        Headers[${JSON.stringify(config.headers)}]`;
        return printable;
    }

    private buildPrintableFromError(error: AxiosError): string {
        let printable = `Method[${error.config.method}], Base[${error.config.baseURL}], Url[${error.config.url}],
            Params[${JSON.stringify(error.config.params)}],
            Data[${JSON.stringify(error.config.data)}],
            Headers[${JSON.stringify(error.config.headers)}]`;
        if (error.response) {
            printable = `Status[${error.response?.status}], ` + printable;
        } else {
            printable = `No response, Code[${error.code}], Message[${error.message}]` + printable;
        }

        return printable;
    }
}
